import { InputHTMLAttributes } from 'react';

import { BackrsIconMap } from '@backrs_ui/common-ui/src/atoms/BackrsIcon';

import { Email, Lock, Visibility, Add, Search } from '@mui/icons-material';
import {
  TextField,
  InputAdornment,
  FormHelperText,
  Box,
  FormLabel,
  Theme,
  SxProps,
} from '@mui/material';

import { appColors } from '../assets/appColors';

/** Icon component map - if you want to use a different icon, please import and add to this list */
export const ICON_TYPES = {
  Email,
  Search,
  Lock,
  Visibility,
  Add,
  Phone: BackrsIconMap.IconPhone,
} as const;

type TextFieldIcon =
  | {
      type: 'LEFT_ICON';
      leftIcon: keyof typeof ICON_TYPES;
      /** A predetermined color is defined, but you can customize it if you want */
      leftIconColor?: string;
      leftIconSize?: number;
      onLeftIconClick?: () => void;
    }
  | {
      type: 'RIGHT_ICON';
      rightIcon: keyof typeof ICON_TYPES;
      /** A predetermined color is defined, but you can customize it if you want */
      rightIconColor?: string;
      rightIconSize?: number;
      onRightIconClick?: () => void;
    }
  | {
      type: 'BOTH_ICON';
      leftIcon: keyof typeof ICON_TYPES;
      /** A predetermined color is defined, but you can customize it if you want */
      leftIconColor?: string;
      leftIconSize?: number;
      rightIcon: keyof typeof ICON_TYPES;
      /** A predetermined color is defined, but you can customize it if you want */
      rightIconColor?: string;
      rightIconSize?: number;
      onLeftIconClick?: () => void;
      onRightIconClick?: () => void;
    };

export type BackrsTextFieldProps = Readonly<{
  id: string;
  variant?: 'SQUARE' | 'ROUNDED';
  name: string;
  value: string;
  label?: string;
  placeholder?: string;
  autoComplete?: string;
  autoFocus?: boolean;
  type: InputHTMLAttributes<HTMLInputElement>['type'];
  onChange: (value: string) => void;
  sx?: SxProps<Theme>;
  inputProps?: InputHTMLAttributes<HTMLInputElement>;
  icon?: TextFieldIcon;
  error?: string;
  helperText?: string;
  divider?: boolean;
  onEnterPress?: () => void;
  disabled?: boolean;
}>;

const getIcon = (
  iconName: keyof typeof ICON_TYPES,
  color: string = appColors.color.darkBlue,
  size?: number,
) => {
  const IconComponent = ICON_TYPES[iconName];

  return <IconComponent style={{ color, fontSize: size }} />;
};

export const BackrsTextField = ({
  value,
  variant = 'SQUARE',
  type,
  onChange,
  icon,
  placeholder,
  label,
  id,
  name,
  autoComplete,
  autoFocus,
  inputProps,
  sx,
  error,
  helperText,
  onEnterPress,
  disabled = false,
}: BackrsTextFieldProps) => {
  /*
   * ICONS
   *
   * We can take advantage of the icon prop's discriminated union type by saying that as long as the icon exists and is not a RIGHT_ICON, we will render the left icon.
   *
   * This means that the left icon will show if we pass in a LEFT_ICON or BOTH_ICON type (and vice versa for the right icon).
   */
  const leftIcon =
    icon && icon?.type !== 'RIGHT_ICON' ? (
      <InputAdornment
        sx={{ cursor: icon.onLeftIconClick ? 'pointer' : 'default' }}
        position="start"
        onClick={icon.onLeftIconClick}
      >
        {getIcon(icon.leftIcon, icon.leftIconColor, icon.leftIconSize)}
      </InputAdornment>
    ) : undefined;

  const rightIcon =
    icon && icon?.type !== 'LEFT_ICON' ? (
      <InputAdornment
        position="end"
        sx={{ cursor: icon.onRightIconClick ? 'pointer' : 'default' }}
        onClick={icon.onRightIconClick}
      >
        {getIcon(icon.rightIcon, icon.rightIconColor, icon.rightIconSize)}
      </InputAdornment>
    ) : undefined;

  const inputStyle = variant === 'SQUARE' ? squaredInputStyle : roundedInputStyle;

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && onEnterPress) {
      onEnterPress();
    }
  };

  return (
    <Box width="100%">
      {label && <FormLabel>{label}</FormLabel>}
      <TextField
        margin="dense"
        fullWidth
        id={id}
        name={name}
        autoComplete={autoComplete}
        autoFocus={autoFocus}
        placeholder={placeholder}
        value={value}
        type={type}
        onChange={(e) => onChange(e.target.value)}
        InputLabelProps={{ shrink: false }}
        error={!!error}
        InputProps={{
          startAdornment: leftIcon,
          endAdornment: rightIcon,
          sx: { width: '100%' },
        }}
        inputProps={inputProps}
        sx={
          {
            ...inputStyle,
            ...sx,
          } as SxProps<Theme>
        }
        disabled={disabled}
        onKeyUp={handleKeyDown}
      />
      {error && <FormHelperText error>{error}</FormHelperText>}
      {helperText && !error && <FormHelperText>{helperText}</FormHelperText>}
    </Box>
  );
};

const squaredInputStyle: SxProps<Theme> = {
  borderRadius: 2,
  backgroundColor: appColors.neutral.mediumGray,
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: 'transparent',
    },
    '&:hover fieldset': {
      borderColor: 'transparent',
    },
    '&.Mui-focused fieldset': {
      borderColor: 'transparent',
    },
    '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    '&.Mui-disabled .MuiSvgIcon-root': {
      opacity: 0.5,
    },
    '&.Mui-disabled svg': {
      opacity: 0.5,
    },
  },
  '&:has(.Mui-disabled)': {
    opacity: 0.5,
  },
} as const;

const sharedAutoFillProps = {
  WebkitBoxShadow: `0 0 0 1000px ${appColors.neutral.white} inset`,
  WebkitTextFillColor: appColors.neutral.black,
  height: 8,
};

const roundedInputStyle: SxProps<Theme> = {
  borderRadius: 8,
  height: 40,
  justifyContent: 'center',
  backgroundColor: appColors.neutral.white,
  alignItems: 'center',
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: 'transparent',
    },
    '&:hover fieldset': {
      borderColor: 'transparent',
    },
    '&.Mui-focused fieldset': {
      borderColor: 'transparent',
    },
  },
  '& input': {
    '&:-webkit-autofill': sharedAutoFillProps,
    '&:-webkit-autofill:hover': sharedAutoFillProps,
    '&:-webkit-autofill:focus': sharedAutoFillProps,
    '&:-webkit-autofill:active': sharedAutoFillProps,
  },
};
